'use client';
import type { CSSProperties, ReactElement } from 'react';
import type { NodeProps, Node } from '@xyflow/react';
import type { FlowchartNodeData } from './flowchart-types';
import { GROUP_LABEL_HEIGHT } from './flowchart-constants';
import { CmarkdownLabelText } from '../Cmarkdowntext/CmarkdownLabelText';
import { FontWeight } from '@/theme/typography';
type FlowchartGroupFlowNode = Node<FlowchartNodeData, 'flowchartGroup'>;
const CONTAINER_RADIUS = 20;
const TITLE_CHIP_STYLE: CSSProperties = {
    position: 'absolute',
    top: -13,
    left: 18,
    height: GROUP_LABEL_HEIGHT - 12,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    borderRadius: 9,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    whiteSpace: 'nowrap',
};
/**
 * The subgraph cluster container, drawn behind its member nodes. On-brand with
 * the node chrome: a dark glass panel tinted with the cluster accent, a faint
 * inset accent ring (echoing the nodes' inset rings), and a clean title chip.
 * Non-interactive — clicks and panning pass through to the canvas/children.
 * Stays at full opacity regardless of hover/focus: it is a backdrop, not a
 * target, so it must not quiet when a member node is hovered or focused.
 */
export function FlowchartGroupNode({ data, id, }: NodeProps<FlowchartGroupFlowNode>): ReactElement {
    const accent = data.accentColor;
    return (
      <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            borderRadius: CONTAINER_RADIUS,
            border: `1px solid color-mix(in srgb, ${accent} 34%, transparent)`,
            backgroundColor: `color-mix(in srgb, ${accent} 6%, rgba(15, 19, 29, 0.55))`,
            boxShadow: [
                `inset 0 0 0 1px color-mix(in srgb, ${accent} 12%, transparent)`,
                `inset 0 1px 0 0 rgba(255, 255, 255, 0.04)`,
                `0 8px 28px -22px color-mix(in srgb, ${accent} 80%, transparent)`,
            ].join(', '),
            pointerEvents: 'none',
        }}>
        {data.label ? (<div style={{
                ...TITLE_CHIP_STYLE,
                backgroundColor: 'rgba(15, 19, 29, 0.92)',
                backgroundImage: 'none',
                border: `1px solid color-mix(in srgb, ${accent} 40%, transparent)`,
            }}>
            <CmarkdownLabelText dataElScope="Cmarkdownflowchart/FlowchartGroupNode" content={data.label} size={11} weight={FontWeight.SemiBold} paragraphStyle={{ marginBottom: 0 }} textStyle={{
                color: `color-mix(in srgb, ${accent} 78%, #ffffff 22%)`,
                whiteSpace: 'nowrap',
                letterSpacing: 0.3,
            }}/>
          </div>) : null}
      </div>
    );
}
